import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  initialFocalPointX?: number;
  initialFocalPointY?: number;
  initialZoom?: number;
  onSave: (focalPointX: number, focalPointY: number, zoom: number) => void;
}

export function ImageCropDialog({
  open,
  onOpenChange,
  imageUrl,
  initialFocalPointX = 0.5,
  initialFocalPointY = 0.5,
  initialZoom = 1,
  onSave,
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialZoom);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);

  const onCropAreaChange = useCallback((area: Area) => {
    setCroppedArea(area);
  }, []);

  const handleSave = () => {
    if (croppedArea) {
      // Convert crop area center to normalized 0-1 coordinates
      const focalPointX = (croppedArea.x + croppedArea.width / 2) / 100;
      const focalPointY = (croppedArea.y + croppedArea.height / 2) / 100;
      onSave(
        Math.max(0, Math.min(1, focalPointX)),
        Math.max(0, Math.min(1, focalPointY)),
        zoom,
      );
    } else {
      onSave(initialFocalPointX, initialFocalPointY, zoom);
    }
    onOpenChange(false);
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Adjust Image Position</DialogTitle>
          <DialogDescription>
            Drag to reposition and use the slider to zoom. This controls which part of the image is visible.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-64 w-full overflow-hidden rounded-md bg-muted">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropAreaChange={onCropAreaChange}
            showGrid={false}
            minZoom={1}
            maxZoom={5}
            objectFit="cover"
          />
        </div>

        <div className="flex items-center gap-3 px-1">
          <span className="text-sm text-muted-foreground shrink-0">Zoom</span>
          <Slider
            value={[zoom]}
            onValueChange={([v]) => setZoom(v)}
            min={1}
            max={5}
            step={0.1}
            className="flex-1"
          />
          <span className="text-sm text-muted-foreground w-10 text-right">{zoom.toFixed(1)}x</span>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleSave}>Save Position</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
